import React, { useEffect, useRef, useState } from "react";
import Pagination from "@mui/material/Pagination";
import { notification, Menu } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faRedo, faSearch, faEye, faFileDownload } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom/dist";
import ThirdPartyNavbar from "../../DashBaord/ThirdPartyNavbar";
import SkeltonList from "../../../components/SkeltonEffect/list";
import { voucherList } from "../../../controllers/V1/winnersControllers";
import { getPage, statusMessage } from "../../../controllers/common";
import LoginPopup from "../Login/LoginPopup";
import PayoutPopup from "./PayoutPopup";
import "../winningVouchers.css";

const AssignedWinningVoucherList = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPayoutPopup, setShowPayoutPopup] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [user, setUser] = useState(() => JSON.parse(sessionStorage.getItem("AGENT-ADMIN-INFO") || "{}"));
  const targetRef = useRef(null);


  const [filterForm, setFilterForm] = useState({
    searchField: "voucher_no",
    searchText: "",
    status: "A",
  });
  const [activeFilters, setActiveFilters] = useState({
    searchField: "voucher_no",
    searchText: "",
    status: "A",
    payout_status: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [vouchers, setVouchers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [skip, setSkip] = useState(0);
  const limit = 10;
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, voucher: null });
  const contextMenuRef = useRef(null);

  const checkAuthentication = () => {
    const currentToken = sessionStorage.getItem("AGENT-TOKEN");
    const currentUser = JSON.parse(sessionStorage.getItem("AGENT-ADMIN-INFO") || "{}");
    setUser(currentUser);
    
    if (currentToken && currentUser?._id) {
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
      navigate('/agent-login');
      return;
    }
    fetchVouchers();
  };

  useEffect(() => {
    document.title = "U-Winn || Assigned Vouchers";
    checkAuthentication();
  }, []);

  

  const fetchVouchers = async () => {
    try {
      if (!isAuthenticated || !user?._id) {
        return;
      }
      setIsLoading(true);
      const condition = {
        ...(activeFilters.payout_status === "Pending" && { 
          $or: [
            { payout_status: { $exists: false } },
            { payout_status: "" },
            { payout_status: "Pending" }
          ]
        }),
        ...(activeFilters.payout_status === "Completed" && { payout_status: "Completed" }),
        status: "A",
        assigned_to: user._id,
      };

      if (activeFilters.searchText) {
        const searchValue = activeFilters.searchText.trim();
        if (activeFilters.searchField === "voucher_no") {
          condition["voucher_no"] = parseInt(searchValue, 10) || searchValue;
        } else if (activeFilters.searchField === "ticket_id") {
          condition["order_id"] = searchValue;
        } else if (activeFilters.searchField === "payment_to") {
          condition["payment_to"] = { $regex: searchValue, $options: "i" };
        } else if (activeFilters.searchField === "purchase_date") {
          condition["purchase_date"] = moment(searchValue).format("YYYY-MM-DD")
        } else if (activeFilters.searchField === "assigned_date") {
          condition["assigned_date"] = {$gte : moment(searchValue).format("YYYY-MM-DD 00:00:01"), $lte : moment(searchValue).format("YYYY-MM-DD 23:59:59")}
        }

      }

      const options = {
        type: "",
        condition,
        select: {},
        sort: { _id: -1 },
        skip,
        limit,
      };

      const res = await voucherList(options);

      if (res?.status) {
        setVouchers(res.result || []);
        setTotalPages(getPage(res.count || 1));
        setTotalCount(res.count || 0);
      } else {
        setVouchers([]);
        setTotalPages(getPage(1));
        setTotalCount(0);
      }
    } catch (error) {
      notification.open({
        message: "Oops!",
        description: "Unable to load assigned vouchers. Please try again.",
        placement: "topRight",
      });
      setVouchers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?._id) {
      fetchVouchers();
      if (targetRef.current) {
        targetRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters, skip, isAuthenticated, showPayoutPopup]);

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    setSkip((page - 1) * limit);
  };

  const handleApplyFilters = () => {
    setActiveFilters({ ...filterForm });
    setCurrentPage(1);
    setSkip(0);
  };

  const handleResetFilters = () => {
    const resetState = {
      searchField: "voucher_no",
      searchText: "",
      status: "A",
    };
    setFilterForm(resetState);
    setActiveFilters(resetState);
    setCurrentPage(1);
    setSkip(0);
  };

  const minItem = vouchers?.length ? skip + 1 : 0;
  const maxItem = Math.min(skip + limit, totalCount);

  // Handle right-click context menu
  const handleContextMenu = (e, voucher) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      voucher: voucher,
    });
  };

  // Close context menu
  const handleCloseContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, voucher: null });
  };

  // Handle context menu actions
  const handleContextMenuAction = (action, voucher) => {
    handleCloseContextMenu();
    
    switch (action) {
      case "settle":
        handleSettleVoucher(voucher);
        break;
      case "view":
        handleViewVoucher(voucher);
        break;
      case "download":
        handleDownloadVoucher(voucher);
        break;
      default:
        break;
    }
  };

  // Handle settle voucher
  const handleSettleVoucher = (voucher) => {
    setSelectedVoucher(voucher);
    setShowPayoutPopup(true);
  };

  // Handle payout popup close
  const handlePayoutPopupClose = () => {
    setShowPayoutPopup(false);
    setSelectedVoucher(null);
    // Refresh the list after payout submission
    // fetchVouchers();
  };

  // Handle view voucher
  const handleViewVoucher = (voucher) => {
    setSelectedVoucher(voucher);
    setShowPayoutPopup(true);
  };

  // Handle download voucher
  const handleDownloadVoucher = (voucher) => {
    // console.log("voucher : ", voucher);
    navigate('/voucher-pdf', {state : voucher});
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(event.target)) {
        handleCloseContextMenu();
      }
    };

    if (contextMenu.visible) {
      document.addEventListener("click", handleClickOutside);
      document.addEventListener("contextmenu", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
      document.removeEventListener("contextmenu", handleClickOutside);
    };
  }, [contextMenu.visible]);

  return (
    <>
      {/* Payout Popup */}
      <PayoutPopup
        popUpOpen={showPayoutPopup}
        togglePopUp={handlePayoutPopupClose}
        voucherData={selectedVoucher}
      />

      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          ref={contextMenuRef}
          style={{
            position: "fixed",
            top: `${contextMenu.y}px`,
            left: `${contextMenu.x}px`,
            zIndex: 1000,
            backgroundColor: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            minWidth: "180px",
            padding: "4px 0",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Menu
            onClick={({ key }) => handleContextMenuAction(key, contextMenu.voucher)}
            style={{ border: "none", boxShadow: "none" }}
            selectable={false}
          >
            {/* <Menu.Item
              key="settle"
              icon={<FontAwesomeIcon icon={faCheckCircle} style={{ color: "#10b981" }} />}
            >
              <span>Settle</span>
            </Menu.Item> */}
            
            <Menu.Item
              key="view"
              icon={<FontAwesomeIcon icon={faEye} style={{ color: "#3b82f6" }} />}
            >
              <span>View</span>
            </Menu.Item>
            {/* <Menu.Item
              key="download"
              icon={<FontAwesomeIcon icon={faFileDownload} style={{ color: "#6366f1" }} />}
            >
              <span>Download</span>
            </Menu.Item> */}
          </Menu>
        </div>
      )}

      {/* Main Content - Only show when authenticated */}
      {isAuthenticated ? (
        <>
          <div className="container-fluid assigned-wrapper" ref={targetRef}>
        <ThirdPartyNavbar title={"Assigned Vouchers"} />
        <div className="card shadow mb-3">
          <div className="card-body">
            <div className="assigned-filters">
              <div className="assigned-filter">
                <label htmlFor="searchField">Search field</label>
                <select
                  id="searchField"
                  className="form-control"
                  value={filterForm.searchField}
                  onChange={(e) =>
                    setFilterForm((prev) => ({ ...prev, searchField: e.target.value }))
                  }
                >
                  <option value="voucher_no">Voucher No.</option>
                  <option value="ticket_id">Ticket ID</option>
                  <option value="payment_to">Payment To</option>
                  <option value="purchase_date">Purchase Date</option>
                  <option value="assigned_date">Assigned Date</option>
                </select>
              </div>
              {["purchase_date","assigned_date"].includes(filterForm.searchField)?(
                <div className="assigned-filter">
                  <label htmlFor="searchText">Search text</label>
                  <div className="input-group">
                    <input
                      id="searchText"
                      type="date"
                      className="form-control"
                      value={filterForm.searchText}
                      onChange={(e) =>
                        setFilterForm((prev) => ({ ...prev, searchText: e.target.value }))
                      }
                    />
                  </div>
                </div>
              ):(
                <div className="assigned-filter">
                  <label htmlFor="searchText">Search text</label>
                  <div className="input-group">
                    <span className="input-group-text" style={{ backgroundColor: "#fff" }}>
                      <FontAwesomeIcon icon={faSearch} />
                    </span>
                    <input
                      id="searchText"
                      type="text"
                      className="form-control"
                      placeholder="Type to search"
                      value={filterForm.searchText}
                      onChange={(e) =>
                        setFilterForm((prev) => ({ ...prev, searchText: e.target.value }))
                      }
                    />
                  </div>
                </div>
              )}
              

              <div className="assigned-filter">
                <label htmlFor="payout_statusFilter">Payout Status</label>
                <select
                  id="payout_statusFilter"
                  className="form-control"
                  value={filterForm.payout_status}
                  onChange={(e) =>
                    setFilterForm((prev) => ({ ...prev, payout_status: e.target.value }))
                  }
                >
                  <option value="" selected={filterForm.payout_status === ""}>All</option>
                  <option value="Completed" selected={filterForm.payout_status === "Completed"}>Completed</option>
                  <option value="Pending" selected={filterForm.payout_status === "Pending"}>Pending</option>
                </select>
              </div>

              
              <div className="assigned-filter assigned-filter-actions">
                <button type="button" className="btn btn-dark" onClick={handleApplyFilters}>
                  <FontAwesomeIcon icon={faFilter} /> Apply
                </button>
                <button type="button" className="btn btn-light" onClick={handleResetFilters}>
                  <FontAwesomeIcon icon={faRedo} /> Reset
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="card shadow mb-4 assigned-desktop">
          <div className="card-body pt-0">
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>Sl. No.</th>
                    <th>Voucher No.</th>
                    <th>Ticket ID</th>
                    <th>Payment To</th>
                    <th>Seller</th>
                    <th>Winning Amount</th>
                    <th>Prepared By</th>
                    <th>Purchase Date</th>
                    <th>Draw Date</th>
                    <th>Assigned Date</th>
                    <th>Payout Status</th>
                    <th>Payout Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan="11">
                        <SkeltonList row={5} col={11} />
                      </td>
                    </tr>
                  ) : vouchers && vouchers.length > 0 ? (
                    vouchers.map((item, index) => (
                      <tr key={item?._id || index} onContextMenu={(e) => handleContextMenu(e, item)} style={{ cursor: "context-menu" }}>
                        <td>
                          {index + 1 + skip}
                        </td>
                        <td>
                          {item?.voucher_no || "-"}
                        </td>
                        <td
                          className="admin_user_list_date"
                        >
                          {item?.order_id || "-"}
                        </td>
                        <td
                          className="admin_user_list_date"
                        >
                          {item?.payment_to || "-"}
                        </td>
                        <td
                          className="admin_user_list_date"
                        >
                          {item?.seller_name || "-"}
                        </td>
                        <td
                          className="admin_user_list_date"
                        >
                          {parseFloat(item?.total_amount || 0)?.toFixed(2)}
                        </td>
                        <td
                          className="admin_user_list_date"
                        >
                          {item?.prepared_by || "-"}
                        </td>
                        <td
                          className="admin_user_list_date"
                        >
                          {item?.purchase_date
                            ? moment(item.purchase_date).format("DD-MM-YYYY")
                            : "-"}
                        </td>
                        <td
                          className="admin_user_list_date"
                        >
                          {item?.draw_date ? moment(item.draw_date).format("DD-MM-YYYY") : "-"}
                        </td>
                        <td
                          className="admin_user_list_date"
                        >
                          {item?.assigned_date ? moment(item.assigned_date).format("DD-MM-YYYY") : "-"}
                        </td>
                        <td
                          className="admin_user_list_date"
                          dangerouslySetInnerHTML={{ __html: statusMessage(item?.payout_status) }}
                        ></td>

                        <td
                          className="admin_user_list_date"
                        >
                          {item?.payout_date ? moment(item?.payout_date).format("YYYY-MM-DD HH:mm") : "--"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center">
                        No vouchers assigned yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="table_pagination_sales_order">
              <p>{`Showing ${minItem}-${maxItem} of ${totalCount} items`}</p>
              <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
            </div>
          </div>
        </div>

        <div className="assigned-mobile">
          <div className="table_pagination_sales_order" style={{ marginTop: "12px" }}>
            <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
          </div>
          {isLoading ? (
            <SkeltonList row={6} col={11} />
          ) : vouchers && vouchers.length > 0 ? (
            <div className="assigned-card-grid">
              {vouchers.map((item, index) => (
                <div
                  className="assigned-card"
                  key={item?._id || index}
                  onContextMenu={(e) => handleContextMenu(e, item)}
                  style={{ cursor: "context-menu" }}
                >
                  <h6>Voucher #{item?.voucher_no || "-"}</h6>
                  <div className="assigned-row">
                    <span>Ticket ID</span>
                    <strong>{item?.order_id || "-"}</strong>
                  </div>
                  <div className="assigned-row">
                    <span>Payment To</span>
                    <strong>{item?.payment_to || "-"}</strong>
                  </div>
                  <div className="assigned-row">
                    <span>Winning Amount</span>
                    <strong>{parseFloat(item?.total_amount || 0)?.toFixed(2)}</strong>
                  </div>
                  <div className="assigned-row">
                    <span>Prepared By</span>
                    <strong>{item?.prepared_by || "-"}</strong>
                  </div>
                  <div className="assigned-row">
                    <span>Purchase Date</span>
                    <strong>
                      {item?.purchase_date ? moment(item.purchase_date).format("DD-MM-YYYY") : "-"}
                    </strong>
                  </div>
                  <div className="assigned-row">
                    <span>Draw Date</span>
                    <strong>
                      {item?.draw_date ? moment(item.draw_date).format("DD-MM-YYYY") : "-"}
                    </strong>
                  </div>
                  <div className="assigned-row">
                    <span>Payout Status</span>
                    <strong
                      dangerouslySetInnerHTML={{ __html: statusMessage(item?.payout_status) }}
                    ></strong>
                  </div>

                  <div className="assigned-row">
                    <span>Payout Date & Time</span>
                    <strong>{item?.payout_date ? moment(item?.payout_date).format("YYYY-MM-DD HH:mm"):"--"}</strong>
                  </div>
                  <div className="assigned-row">
                    <button className="btn btn-success" style={{ width: "100%", marginTop: "10px", borderRadius: "8px" }} onClick={() => handleViewVoucher(item)}>
                      <FontAwesomeIcon icon={faEye} /> View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="card shadow-sm p-3 text-center">No vouchers assigned yet.</div>
          )}
          <div className="table_pagination_sales_order" style={{ marginTop: "12px" }}>
            <p>{`Showing ${minItem}-${maxItem} of ${totalCount} items`}</p>
            <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
          </div>
        </div>
      </div>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            backgroundColor: "#f9fafb",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress sx={{ color: "#667eea" }} />
            <Typography sx={{ marginTop: 2, color: "#6b7280" }}>
              Please wait...
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default AssignedWinningVoucherList;
